import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import { getMsalConfiguration, MSAL_TOKEN_REQUEST } from "./config";

let msalInstance: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

const getMsal = () => {
  msalInstance ??= new PublicClientApplication(getMsalConfiguration());
  return msalInstance;
};

/** Возвращать после входа можно только внутрь приложения — иначе returnTo превращается в открытый редирект. */
const toSafeReturnPath = (path: string) => (path.startsWith("/") && !path.startsWith("//") ? path : "/");

/**
 * Старт MSAL. Сначала обязательно initialize(): без него любой другой вызов падает
 * с uninitialized_public_client_application. Затем handleRedirectPromise(): на возврате из Microsoft
 * он разбирает ответ и сам уводит на страницу, с которой начинался вход (redirectStartPage).
 * Повторные вызовы получают тот же промис.
 */
export const initMsal = (): Promise<void> => {
  initPromise ??= (async () => {
    const msal = getMsal();

    await msal.initialize();
    const result = await msal.handleRedirectPromise();

    msal.setActiveAccount(result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null);
  })();

  return initPromise;
};

export const getMsalAccount = (): AccountInfo | null => getMsal().getActiveAccount();

/** Вход через редирект на Microsoft. returnTo — путь в приложении, куда вернуться после входа. */
export const loginWithMicrosoft = (returnTo = "/"): Promise<void> =>
  getMsal().loginRedirect({
    ...MSAL_TOKEN_REQUEST,
    redirectStartPage: new URL(toSafeReturnPath(returnTo), window.location.origin).href,
  });

/** Когда в последний раз восстанавливали сессию редиректом — чтобы не уйти в бесконечный цикл. */
const SILENT_RECOVERY_KEY = "auth.silentRecoveryAt";
const SILENT_RECOVERY_COOLDOWN_MS = 60_000;

/** Можно ли сейчас уйти на восстановление сессии: не чаще раза в минуту на вкладку. */
const claimSilentRecovery = (): boolean => {
  try {
    const lastAttempt = Number(sessionStorage.getItem(SILENT_RECOVERY_KEY) || 0);

    if (Date.now() - lastAttempt < SILENT_RECOVERY_COOLDOWN_MS) return false;

    sessionStorage.setItem(SILENT_RECOVERY_KEY, String(Date.now()));
    return true;
  } catch {
    // без sessionStorage защиту от цикла не построить — лучше показать ошибку, чем редиректить по кругу
    return false;
  }
};

/**
 * Проверка сессии на старте. Refresh token у SPA живёт 24 часа, дальше MSAL продлевает сессию через
 * скрытый iframe, а его режут блокировки сторонних cookie. Popup без клика пользователя браузер
 * тоже заблокирует, поэтому на старте уходим в loginRedirect тем же аккаунтом: по сессии Microsoft
 * он проходит без экрана логина. Если и это не помогло за последнюю минуту — отдаём ошибку наверх.
 */
export const ensureAzureSession = async (): Promise<void> => {
  const msal = getMsal();
  const account = msal.getActiveAccount();

  if (!account) return;

  try {
    await msal.acquireTokenSilent({ ...MSAL_TOKEN_REQUEST, account });
  } catch (error) {
    if (!claimSilentRecovery()) throw error;

    console.warn("[Auth] Silent token renewal failed, signing in again via redirect", error);
    await msal.loginRedirect({
      ...MSAL_TOKEN_REQUEST,
      loginHint: account.username,
      redirectStartPage: window.location.href,
    });

    // страница уже уходит в Microsoft: не отпускаем старт, иначе на мгновение мелькнул бы экран логина
    await new Promise<never>(() => {});
  }
};

export interface IGetAzureTokenOptions {
  /** После 401 кэшированный токен мог быть отозван — идём за новым в обход кэша MSAL. */
  forceRefresh?: boolean;
}

/**
 * Azure access token — для всех запросов к бэкенду и для обмена на внутренний JWT.
 * MSAL сам кэширует и обновляет токены, поэтому его вызывают перед каждым запросом, а не хранят руками.
 */
export const getAzureToken = async ({ forceRefresh = false }: IGetAzureTokenOptions = {}): Promise<string> => {
  const msal = getMsal();
  const account = msal.getActiveAccount();

  if (!account) throw new Error("You are not signed in with Microsoft.");

  try {
    const { accessToken } = await msal.acquireTokenSilent({ ...MSAL_TOKEN_REQUEST, account, forceRefresh });
    return accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error;

    // тихо токен не выдадут: нужно согласие или повторный вход в окне Microsoft
    const { accessToken } = await msal.acquireTokenPopup({ ...MSAL_TOKEN_REQUEST, account });
    return accessToken;
  }
};

/**
 * Выход через Microsoft. Просто стереть токены мало: сессия Microsoft останется,
 * и следующий вход молча пройдёт под тем же аккаунтом.
 */
export const logoutFromMicrosoft = (): Promise<void> => {
  const msal = getMsal();
  return msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
};
